import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { MenuIcon, XIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useSignedIn } from '../../contexts/AccountContext';
import { shopLabels } from '../../data/shopLabels';
import { mainNav } from '../../data/navigation';
import { Logo } from './Logo';
import { AnnouncementBar } from './AnnouncementBar';
import { CartButton } from './CartButton';
import { MobileMenu } from './MobileMenu';

/** Vanaf hier krijgt de header een eigen vlak en een haarlijn. */
const SOLID_AFTER = 8;
/** Boven deze positie blijft de header altijd staan. */
const HIDE_AFTER = 240;
/** Zoveel px in één richting voordat de header verdwijnt of terugkomt: een trillend trackpad doet niets. */
const HIDE_DISTANCE = 48;
const SHOW_DISTANCE = 24;
const iconBtn = 'relative flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors duration-150 ease-soft hover:bg-ink/[0.06]';

/**
 * Sticky header met een vaste hoogte (72px). Hij krimpt niet meer bij scrollen: dat gaf
 * verspringende inhoud en vroeg om drempel-trucs. Alleen het vlak verandert (sand → shell met
 * haarlijn). Verdwijnt bij naar beneden scrollen en komt direct terug bij omhoog scrollen;
 * blijft staan als het menu open is of de toetsenbordfocus erin staat.
 *
 * Computer: logo · navigatie · huidadvies, account, winkelmand en de hoofdknop "Afspraak maken"
 * (de salon is de helft van het bedrijf en hoort altijd één klik weg).
 * Telefoon: logo · winkelmand · menu. Huidadvies en boeken staan in het menu en de actiebalk.
 */
export function Header() {
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  const [hidden, setHidden] = useState(false);
  const {
    pathname
  } = useLocation();
  // Alleen stabiele acties: de header tekent niet opnieuw als de winkelmand verandert.
  const {
    openCart,
    openQuiz,
    openBooking
  } = useOverlayActions();
  const signedIn = useSignedIn();
  const menuId = useId();
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const accountLabel = signedIn ? 'Mijn omgeving' : 'Inloggen';
  useEffect(() => {
    setOpen(false);
    setHidden(false);
  }, [pathname]);
  useEffect(() => {
    let last = 0;
    let dir: 1 | -1 = 1;
    /** Waar de huidige scrollrichting begon. */
    let anchor = 0;
    let frame = 0;

    // iOS "stuitert" voorbij boven- en onderkant; die waarden tellen niet mee.
    const position = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      return Math.min(Math.max(window.scrollY, 0), Math.max(max, 0));
    };

    // Hooguit één keer per beeld; React tekent alleen opnieuw als een stand echt omslaat.
    const update = () => {
      frame = 0;
      const y = position();
      setSolid(y > SOLID_AFTER);
      if (y !== last) {
        const nextDir = y > last ? 1 : -1;
        if (nextDir !== dir) {
          dir = nextDir;
          anchor = last;
        }
        last = y;
      }
      if (y <= HIDE_AFTER) setHidden(false);else if (dir === 1 && y - anchor >= HIDE_DISTANCE) setHidden(true);else if (dir === -1 && anchor - y >= SHOW_DISTANCE) setHidden(false);
    };
    last = anchor = position();
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Menu open: Escape sluit en zet de focus terug; de pagina eronder scrolt niet mee.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      toggleRef.current?.focus();
    };
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const runAndClose = (action: () => void) => () => {
    setOpen(false);
    action();
  };
  return <div
  // Focus in de header (Tab vanaf de skiplink) mag nooit achter een verborgen header vallen.
  onFocus={() => setHidden(false)} className={`sticky top-0 z-header transition-transform duration-300 ease-soft motion-reduce:transition-none ${hidden && !open ? '-translate-y-full' : 'translate-y-0'}`} data-id="element-7828" data-mp="2bm30sc7vw2uf">
      <AnnouncementBar data-id="element-7829" data-mp="2z8rm77w3qdfs" />

      <header className={`relative border-b transition-[background-color,border-color] duration-200 ease-soft ${solid || open ? 'border-line bg-shell' : 'border-transparent bg-sand'}`} data-id="element-7830" data-mp="2ubhwdr4svzrf">
        <div className="mx-auto flex h-[72px] max-w-content items-center justify-between gap-6 px-6 lg:px-10" data-id="element-7831" data-mp="1ivt72t42qong">
          <Logo className="h-9 lg:h-10" data-id="element-7832" data-mp="18tgxgobyuin3" />

          <nav aria-label="Hoofdnavigatie" className="hidden items-center gap-5 lg:flex xl:gap-7" data-id="element-7833" data-mp="18qb95bb5vksf">
            {mainNav.map(item => <NavLink key={item.to} to={item.to} className={({
            isActive
          }) => `group relative whitespace-nowrap py-2 text-[14px] transition-colors duration-150 ease-soft hover:text-ink ${isActive ? 'text-ink' : 'text-muted'}`} data-id="element-7834" data-mp="1iq21p7g5vppv">
                {({
              isActive
            }) => <>
                    {item.label}
                    <span aria-hidden="true" className={`absolute bottom-0.5 left-0 h-px w-full origin-left bg-clay transition-transform duration-200 ease-soft ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`} data-id="element-7835" data-mp="21i4l2qk6tk3t" />
                  </>}
              </NavLink>)}
          </nav>

          <div className="hidden items-center gap-1 lg:flex" data-id="element-7836" data-mp="43js57uxxjnt">
            <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full px-3 text-[14px] text-ink transition-colors duration-150 ease-soft hover:bg-ink/[0.06]" data-id="element-7837" data-mp="3v8tln9i58g26">
              <SparklesIcon className="h-4 w-4 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7838" data-mp="myqoacqhzi41" />
              {/* Op smallere schermen alleen het icoon; de naam blijft voor schermlezers. */}
              <span className="sr-only xl:not-sr-only" data-id="element-7839" data-mp="wotfq8hz0o09">Huidadvies</span>
            </button>
            <Link to="/account" aria-label={accountLabel} title={accountLabel} className={iconBtn} data-id="element-7840" data-mp="rs30fndzpwfw">
              <UserIcon className="h-[18px] w-[18px]" strokeWidth={1.5} aria-hidden="true" data-id="element-7841" data-mp="15ruhxy3ptuyn" />
              {signedIn && <span aria-hidden="true" className="absolute right-2 top-2 h-2 w-2 rounded-full bg-clay ring-2 ring-shell" data-id="element-7842" data-mp="11qipzlxdcxna" />}
            </Link>
            <CartButton onClick={openCart} data-id="element-7843" data-mp="1kbov22ve8d8z" />
            <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className="ml-2 inline-flex h-10 items-center whitespace-nowrap rounded-full bg-ink px-5 text-[14px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98]" data-id="element-7844" data-mp="1pbjqmjq45r0d">
              Afspraak maken
            </button>
          </div>

          <div className="-mr-2 flex items-center gap-1 lg:hidden" data-id="element-7845" data-mp="16f1rvicukqum">
            <CartButton onClick={openCart} data-id="element-7846" data-mp="sndgxiec48f8" />
            <button ref={toggleRef} type="button" aria-label={shopLabels.menu(open)} aria-expanded={open} aria-controls={menuId} onClick={() => setOpen(v => !v)} className={iconBtn} data-id="element-7847" data-mp="skiuxmfoafjp">
              {open ? <XIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-7848" data-mp="135a7jcgderg5" /> : <MenuIcon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" data-id="element-7849" data-mp="134qr606dcb48" />}
            </button>
          </div>
        </div>

        <AnimatePresence initial={false} data-id="element-7850" data-mp="u4kf8iqbjj76">
          {open && <MobileMenu id={menuId} accountLabel={accountLabel} onQuiz={runAndClose(openQuiz)} onBooking={runAndClose(() => openBooking())} data-id="element-7851" data-mp="u2r68ws8gmw0" />}
        </AnimatePresence>
      </header>
    </div>;
}